// frontend/src/components/SettlementStatus.js
import React from 'react';
import { Clock, CheckCircle, XCircle, AlertCircle, Loader } from 'lucide-react';
import { formatCurrency } from '../utils/settlementUtils';

const SettlementStatus = ({ bet }) => {
  const getStatusConfig = (status) => {
    switch(status) {
      case 'Won':
        return {
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
          borderColor: 'border-green-500/20',
          icon: <CheckCircle size={16} className="text-green-500" />
        };
      case 'Lost':
        return {
          color: 'text-red-500',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/20',
          icon: <XCircle size={16} className="text-red-500" />
        };
      case 'Pending':
        return {
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-500/10',
          borderColor: 'border-yellow-500/20',
          icon: <Clock size={16} className="text-yellow-500" />
        };
      case 'Partially_Won':
        return {
          color: 'text-blue-500',
          bgColor: 'bg-blue-500/10',
          borderColor: 'border-blue-500/20',
          icon: <AlertCircle size={16} className="text-blue-500" />
        };
      default:
        return {
          color: 'text-gray-500',
          bgColor: 'bg-gray-500/10',
          borderColor: 'border-gray-500/20',
          icon: <Loader size={16} className="text-gray-500" />
        };
    }
  };

  const statusConfig = getStatusConfig(bet.status);
  const progressPercentage = bet.settlement?.progressPercentage || 0;
  const canSettle = bet.settlement?.canSettle || false;
  const pendingPicks = bet.settlement?.pendingPicks || 0;

  return (
    <div className={`p-4 rounded-lg border ${statusConfig.borderColor} ${statusConfig.bgColor}`}>
      {/* Status Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {statusConfig.icon}
          <span className={`font-semibold ${statusConfig.color}`}>
            {bet.status.replace('_', ' ')}
          </span>
        </div>
        <div className="text-sm text-gray-400">
          Bet #{bet.id}
        </div>
      </div>

      {/* Progress Bar - Only show for pending bets */}
      {bet.status === 'Pending' && progressPercentage < 100 && (
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>Settlement Progress</span>
            <span>{progressPercentage.toFixed(1)}%</span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Settlement Details */}
      <div className="space-y-2">
        {/* Bet Amount and Payout */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-400">Bet Amount:</span>
          <span className="text-sm font-medium text-white">
            {formatCurrency(bet.amount)}
          </span>
        </div>

        {bet.potentialPayout && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-400">Potential Payout:</span>
            <span className="text-sm font-medium text-green-400">
              {formatCurrency(bet.potentialPayout)}
            </span>
          </div>
        )}

        {bet.actualPayout && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-400">Actual Payout:</span>
            <span className={`text-sm font-medium ${bet.actualPayout > 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(bet.actualPayout)}
            </span>
          </div>
        )}
      </div>

      {/* Settlement Status Message */}
      <div className="mt-3 pt-3 border-t border-gray-600">
        <div className="text-sm text-gray-400">
          {canSettle ? (
            <div className="flex items-center gap-1 text-green-400">
              <CheckCircle size={14} />
              Ready for settlement
            </div>
          ) : bet.status === 'Pending' ? (
            <div className="flex items-center gap-1">
              <Clock size={14} />
              {pendingPicks > 0 
                ? `${pendingPicks} pick${pendingPicks !== 1 ? 's' : ''} pending`
                : 'Awaiting game results'}
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {statusConfig.icon}
              Settlement complete
            </div>
          )}
        </div>

        {/* Estimated Settlement Time */}
        {bet.settlement?.estimatedSettlement && bet.status === 'Pending' && (
          <div className="text-xs text-gray-500 mt-1">
            Est. settlement: {new Date(bet.settlement.estimatedSettlement).toLocaleString()}
          </div>
        )}
      </div>

      {/* Pick Status Summary for multi-pick bets */}
      {bet.picks && bet.picks.length > 1 && (
        <div className="mt-3 pt-3 border-t border-gray-600">
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Picks:</span>
            <div className="flex gap-3">
              {bet.settlement?.winningPicks > 0 && (
                <span className="text-green-400">
                  {bet.settlement.winningPicks} Won
                </span>
              )}
              {bet.settlement?.losingPicks > 0 && (
                <span className="text-red-400">
                  {bet.settlement.losingPicks} Lost
                </span>
              )}
              {bet.settlement?.pendingPicks > 0 && (
                <span className="text-yellow-400">
                  {bet.settlement.pendingPicks} Pending
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettlementStatus;